/**
 * Endpoints Manager
 * -----------------------------------------------------------------------------
 * Creates and configures the endpoints manager for ECS client.
 * Handles telemetry and global context for ECS operations.
 */

import type { IEndpointsManager, IGlobalContext, ITelemetryData } from "@1js/endpoints-manager";
import { createInstance } from "@1js/endpoints-manager";
import { logDebug, logInfo } from "./logger";

/**
 * Global Context implementation using browser APIs.
 */
export class GlobalContext implements IGlobalContext {
  setTimeout(callback: () => void, timeInMs: number): number {
    const timeoutId = window.setTimeout(callback, timeInMs);
    logDebug(`Set timeout: ${timeoutId} for ${timeInMs}ms`, 'TIMEOUT');
    return timeoutId;
  }

  clearTimeout(timeOutKey: number): void {
    window.clearTimeout(timeOutKey);
    logDebug(`Cleared timeout: ${timeOutKey}`, 'TIMEOUT');
  }
}

/**
 * Telemetry data consumer for ECS client operations.
 */
export function consumeTelemetryData(data: ITelemetryData): void {
  logInfo('ECS Client telemetry data received', 'TELEMETRY', {
    timestamp: new Date().toISOString(),
    data: data
  });
  
  // Here you could send telemetry to your analytics service
  // Example: analyticsService.track('ecs_telemetry', data);
}

/**
 * Create an endpoints manager instance with proper configuration.
 */
export function createEndpointsManager(): IEndpointsManager<unknown> {
  logDebug('Creating endpoints manager instance', 'ENDPOINTS');
  
  const endpointsManager = createInstance<unknown>(
    {}, // Empty configuration object - will be populated by ECS client
    {
      globalContext: new GlobalContext(),
      telemetryProvider: consumeTelemetryData,
      configuration: {
        // Model initialization timeout (how long to wait for model to load)
        modelInitializationTimeoutInMs: 1000,
        
        // Base URL resolution timeout (how long to wait for endpoint resolution)
        baseUrlResolutionTimeoutInMs: 3000,
        
        // Default request timeout for individual requests
        defaultRequestTimeoutInMs: 4000,
      },
    }
  );

  logDebug('Endpoints manager created successfully', 'ENDPOINTS');
  return endpointsManager;
}

/**
 * Get endpoints manager configuration summary for debugging.
 */
export function getEndpointsManagerConfig(): {
  modelInitializationTimeoutInMs: number;
  baseUrlResolutionTimeoutInMs: number;
  defaultRequestTimeoutInMs: number;
} {
  return {
    modelInitializationTimeoutInMs: 1000,
    baseUrlResolutionTimeoutInMs: 3000,
    defaultRequestTimeoutInMs: 4000,
  };
}